import React from "react";
import { LeagueFixture } from "../../api/leagues";
import ClubBadge from "../ClubBadge";
import { getClubBadge } from "../../data/clubBadges";

export const UK_TZ = "Europe/London";

export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", timeZone: UK_TZ });
}

export function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: UK_TZ });
}

export function dateKey(iso: string): string {
  return new Date(iso).toLocaleDateString("en-CA", { timeZone: UK_TZ });
}

export function groupByDate(fixtures: LeagueFixture[]): { key: string; label: string; fixtures: LeagueFixture[] }[] {
  const map = new Map<string, { label: string; fixtures: LeagueFixture[] }>();
  for (const f of fixtures) {
    const key = f.kickoffTime ? dateKey(f.kickoffTime) : "tbd";
    if (!map.has(key)) {
      map.set(key, { label: f.kickoffTime ? formatDate(f.kickoffTime) : "Date TBD", fixtures: [] });
    }
    map.get(key)!.fixtures.push(f);
  }
  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, v]) => ({ key, ...v }));
}

export const HIGHLIGHT_COLOR = "oklch(0.72 0.18 295)";

type SpOutcome = "exact" | "correct" | "wrong" | "none";

function spOutcome(fixture: LeagueFixture): SpOutcome {
  const pred = fixture.myPrediction;
  if (!pred || fixture.status !== "FINISHED" || fixture.homeScore == null || fixture.awayScore == null) return "none";
  if (pred.homeScore === fixture.homeScore && pred.awayScore === fixture.awayScore) return "exact";
  const predDir = Math.sign(pred.homeScore - pred.awayScore);
  const actualDir = Math.sign(fixture.homeScore - fixture.awayScore);
  return predDir === actualDir ? "correct" : "wrong";
}

const SP_OUTCOME_STRIPE: Record<SpOutcome, string> = {
  exact:   "oklch(0.72 0.19 145)",
  correct: "oklch(0.80 0.15 85)",
  wrong:   "oklch(0.60 0.20 25)",
  none:    "transparent",
};

export function FixtureRow({
  fixture,
  highlightedClubIds,
  spMode = false,
  isResultsView = false,
}: {
  fixture: LeagueFixture;
  highlightedClubIds?: Set<string>;
  spMode?: boolean;
  isResultsView?: boolean;
}) {
  const isFinished = fixture.status === "FINISHED";
  const homeCfg = getClubBadge(fixture.homeClub.name);
  const awayCfg = getClubBadge(fixture.awayClub.name);
  const homeName = fixture.homeClub.shortName ?? fixture.homeClub.name;
  const awayName = fixture.awayClub.shortName ?? fixture.awayClub.name;
  const homeHighlighted = highlightedClubIds?.has(fixture.homeClub.id) ?? false;
  const awayHighlighted = highlightedClubIds?.has(fixture.awayClub.id) ?? false;

  const outcome = spMode && isResultsView ? spOutcome(fixture) : "none";
  const rowBg = (homeHighlighted || awayHighlighted) ? "color-mix(in srgb, oklch(0.72 0.18 295) 8%, transparent)" : undefined;
  const stripeColor = SP_OUTCOME_STRIPE[outcome];

  return (
    <div>
      <div style={{
        display: "grid",
        gridTemplateColumns: "1fr auto 1fr",
        alignItems: "center",
        gap: "0.3rem",
        padding: "0.3rem 0.9rem",
        borderBottom: "1px solid var(--color-border-subtle)",
        borderLeft: `3px solid ${stripeColor}`,
        background: rowBg,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.3rem", justifyContent: "flex-end" }}>
          <span style={{ fontSize: "0.72rem", fontWeight: homeHighlighted ? 700 : 600, color: homeHighlighted ? HIGHLIGHT_COLOR : "var(--color-text)", textAlign: "right" }}>{homeName}</span>
          {homeCfg && <ClubBadge config={homeCfg} size={13} title={fixture.homeClub.name} />}
        </div>

        <div style={{ textAlign: "center", minWidth: 48, flexShrink: 0 }}>
          {isFinished ? (
            <span style={{ fontFamily: "var(--font-display)", fontSize: "0.85rem", fontWeight: 800, color: "var(--color-text)", letterSpacing: "0.04em" }}>
              {fixture.homeScore ?? 0} – {fixture.awayScore ?? 0}
            </span>
          ) : (
            <span style={{ fontSize: "0.68rem", fontWeight: 600, color: "var(--color-text-muted)" }}>
              {fixture.kickoffTime ? formatTime(fixture.kickoffTime) : "–"}
            </span>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
          {awayCfg && <ClubBadge config={awayCfg} size={13} title={fixture.awayClub.name} />}
          <span style={{ fontSize: "0.72rem", fontWeight: awayHighlighted ? 700 : 600, color: awayHighlighted ? HIGHLIGHT_COLOR : "var(--color-text)" }}>{awayName}</span>
        </div>
      </div>
      {isFinished && fixture.decidedBy === "PENALTIES" && fixture.penaltyHomeScore !== null && fixture.penaltyAwayScore !== null && (
        <div style={{ textAlign: "center", fontSize: "0.58rem", color: "var(--color-text-dim)", padding: "0.1rem 0 0.2rem", borderBottom: "1px solid var(--color-border-subtle)" }}>
          (pens {fixture.penaltyHomeScore}–{fixture.penaltyAwayScore})
        </div>
      )}
    </div>
  );
}
